import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { apiFetch } from '../lib/api';
import { getDefaultPortalPath, userHasPermission } from '../lib/portalAccess';
import { resetPreferencesCache } from './usePreferences';

const AuthContext = createContext(null);

const DEFAULT_PROVIDERS = {
  microsoftEnabled: false,
  microsoftLoginUrl: null,
  googleEnabled: false,
  googleLoginUrl: null,
  localEnabled: false,
  redirectUri: '',
};

// Role to permissions mapping for impersonation
const IMPERSONATION_PERMISSIONS = {
  utilisateur: ['dashboard.view', 'ticketing.view', 'documents.view'],
  superviseur: ['dashboard.view', 'ticketing.view', 'ticketing.manage', 'documents.view', 'reports.view'],
  comptable: ['dashboard.view', 'bi.view', 'documents.view', 'reports.view'],
  directeur: ['dashboard.view', 'ticketing.view', 'ticketing.manage', 'documents.view', 'reports.view', 'bi.view', 'sites.view'],
};

// Helper to get impersonated user from session storage
function getImpersonatedUser() {
  try {
    const saved = sessionStorage.getItem('impersonated_user');
    return saved ? JSON.parse(saved) : null;
  } catch { return null; }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [providers, setProviders] = useState(DEFAULT_PROVIDERS);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const response = await apiFetch('/api/auth/me');
      if (!response.ok) {
        setUser(null);
        return null;
      }
      const data = await response.json();
      if (data.ok && data.user) {
        setUser(data.user);
        return data.user;
      }
      setUser(null);
      return null;
    } catch {
      setUser(null);
      return null;
    }
  }, []);

  const loadProviders = useCallback(async () => {
    try {
      const response = await apiFetch('/api/auth/providers');
      const data = await response.json();
      if (!data.ok) return;
      setProviders({
        microsoftEnabled: Boolean(data.microsoft_enabled),
        microsoftLoginUrl: data.microsoft_login_url || null,
        googleEnabled: Boolean(data.google_enabled),
        googleLoginUrl: data.google_login_url || null,
        localEnabled: Boolean(data.local_enabled),
        redirectUri: data.redirect_uri || '',
      });
    } catch {
      setProviders(DEFAULT_PROVIDERS);
    }
  }, []);

  const loginLocal = useCallback(async (username, password) => {
    const response = await apiFetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data.ok) {
      throw new Error(data.error || 'Connexion impossible');
    }

    // Changement de compte sans déconnexion (session expirée, puis un collègue se connecte dans
    // le même onglet) : les préférences en cache sont celles du compte précédent.
    resetPreferencesCache();
    const authenticatedUser = await refresh();
    return authenticatedUser || data.user || null;
  }, [refresh]);

  const logout = useCallback(async () => {
    try {
      await apiFetch('/api/auth/logout', { method: 'POST' });
    } catch {
      // Ignore logout transport errors and clear local auth state anyway.
    }
    // Le compte suivant dans cet onglet n'hérite pas des préférences de celui-ci (visites
    // guidées comprises).
    resetPreferencesCache();
    setUser(null);
  }, []);

  // Session expirée détectée par apiFetch (401 hors /api/auth/*) : on rejoue
  // /api/auth/me une seule fois à la fois ; s'il échoue, user passe à null et
  // ProtectedRoute redirige vers /login avec returnTo. Sans ce pont, le portail
  // restait « connecté » avec un cookie mort jusqu'au prochain F5.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    let checking = false;
    const onUnauthorized = () => {
      if (checking || !user) return;
      checking = true;
      refresh().finally(() => { checking = false; });
    };
    window.addEventListener('api-unauthorized', onUnauthorized);
    return () => window.removeEventListener('api-unauthorized', onUnauthorized);
  }, [user, refresh]);

  useEffect(() => {
    let active = true;

    async function bootstrapAuth() {
      await loadProviders();
      const nextUser = await refresh();
      if (!active && nextUser) return;
      if (active) setLoading(false);
    }

    bootstrapAuth();
    return () => {
      active = false;
    };
  }, [loadProviders, refresh]);

  // Get the effective user (impersonated or real)
  const impersonatedUser = getImpersonatedUser();
  const effectiveUser = impersonatedUser ? {
    ...user,
    id: impersonatedUser.id || user?.id,
    username: impersonatedUser.username || user?.username,
    email: impersonatedUser.email || user?.email,
    name: impersonatedUser.name || user?.name,
    client_id: impersonatedUser.client_id || user?.client_id,
    client_scope: impersonatedUser.client_scope || user?.client_scope || 'self',
    // Override with impersonated user's role and permissions
    role: impersonatedUser.role,
    permissions: IMPERSONATION_PERMISSIONS[impersonatedUser.role] || [],
    impersonated: true,
    impersonatedName: impersonatedUser.name,
    // Qui se fait passer pour un autre n'est pas le dirigeant : l'accueil suit le compte AFFICHÉ.
    est_dirigeant: false,
  } : user;

  const value = {
    user,
    realUser: user, // Always the real logged-in user
    effectiveUser, // The user we're acting as (could be impersonated)
    providers,
    loading,
    isAuthenticated: Boolean(user),
    isImpersonating: Boolean(impersonatedUser),
    refresh,
    loginLocal,
    logout,
    hasPermission: (permission, orgCode) => userHasPermission(effectiveUser, permission, orgCode),
    defaultPath: getDefaultPortalPath(effectiveUser),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider');
  }
  return context;
}

// Variante tolérante pour un ENCART (pas une page) : `null` hors AuthProvider au
// lieu de lever. Un composant secondaire rendu dans une page testée « nue » retombe
// alors en lecture seule sans faire tomber la page — même repli que `useToast`
// sans ToastProvider. Aucun droit n'est accordé par ce chemin : sans contexte,
// `hasPermission` n'existe pas, donc rien n'est permis.
export function useOptionalAuth() {
  return useContext(AuthContext);
}
